import i18next from 'i18next';
import { getStatusStr } from '../translations';
import { GoalBase, GoalStatus, GoalViewBy, NULL_CATEGORY_ID } from '../types';

type GroupKey = {
  id: string;
  name: string;
};

const STATUS_ORDER: GoalStatus[] = [
  'DRAFT',
  'BEHIND',
  'ON_TRACK',
  'DONE',
  'CANCELED',
];

// Group and sort goals by category and statuses
export function groupGoals(
  goals: GoalBase[],
  viewBy: GoalViewBy,
): Map<string, GoalBase[]> {
  const sortedGoals = [...goals].sort((a, b) => compareGoal(a, b, viewBy));
  return sortedGoals.reduce((acc, goal) => {
    const groupKey =
      viewBy === 'status'
        ? { id: goal.status, name: getStatusStr(goal.status) }
        : {
            id: goal.category?.id ?? NULL_CATEGORY_ID,
            name:
              goal.category?.name ??
              i18next.t('performanceReview.goals2.goalsFilter.noCategory') ??
              '',
          };
    const groupKeyStr = serializeGroupKey(groupKey);
    acc.set(groupKeyStr, [...(acc.get(groupKeyStr) || []), goal]);
    return acc;
  }, new Map<string, GoalBase[]>());
}

export function getGroupName(groupKeyStr: string): string {
  const groupKey = deserializeGroupKey(groupKeyStr);
  return groupKey.name;
}

export function getGroupId(groupKeyStr: string): string {
  const groupKey = deserializeGroupKey(groupKeyStr);
  return groupKey.id;
}

const getStatusOrder = (status: GoalStatus) => {
  const idx = STATUS_ORDER.indexOf(status);
  return idx === -1 ? STATUS_ORDER.length : idx;
};

const compareGoal = (a: GoalBase, b: GoalBase, viewBy: GoalViewBy) => {
  if (viewBy === 'category') {
    const categoryCompare = compareGoalByCategory(a, b);
    if (categoryCompare !== 0) {
      return categoryCompare;
    }
    return getStatusOrder(a.status) - getStatusOrder(b.status);
  } else if (viewBy === 'status') {
    const statusOrder = getStatusOrder(a.status) - getStatusOrder(b.status);
    if (statusOrder !== 0) {
      return statusOrder;
    }
    return compareGoalByCategory(a, b);
  }
  return 0;
};

const compareGoalByCategory = (a: GoalBase, b: GoalBase) => {
  const nameA = a.category?.name?.toLowerCase() ?? '';
  const nameB = b.category?.name?.toLowerCase() ?? '';
  if (nameA === '' && nameB !== '') {
    return 1;
  }
  if (nameA !== '' && nameB === '') {
    return -1;
  }
  if (nameA !== nameB) {
    return nameA.localeCompare(nameB);
  }
  return 0;
};

export const DELIMETER = '<@#!:::>';

const serializeGroupKey = (groupKey: GroupKey): string => {
  return `${groupKey.id}${DELIMETER}${groupKey.name}`;
};

const deserializeGroupKey = (serializedKey: string): GroupKey => {
  const [id, name] = serializedKey.split(DELIMETER);
  return { id, name };
};
